import React, { useState, useMemo, useEffect, useRef, ReactNode } from 'react';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { Config } from '@/config';
import {
  errorModalTextRecoil,
  completeModalRecoil,
  modifyModalRecoil,
  siteModifyModalRecoil,
} from '@/atom/ModalAtom';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import LS from '@/styles/layoutStyled';
import CS from '@/styles/componentStyled';
import BS from '@/styles/btnStyled';
import IS from '@/styles/inputStyled';
import ModalType1 from '@/modal/ModalType1';
import { SiteApi } from '@/sdk/SiteApi';
import {
  UpdateSiteDtoTx,
  SiteControllerCheckSiteParams,
} from '@/sdk/data-contracts';

interface SiteModifyModalProps {
  id: number;
}

const SiteModifyModal = ({ id }: SiteModifyModalProps) => {
  const siteApi = new SiteApi({ baseURL: Config.API_URL });
  const navigate = useNavigate();

  const setErrorModalText = useSetRecoilState(errorModalTextRecoil);
  const setCompleteModalState = useSetRecoilState(completeModalRecoil);
  const setSiteModifyModalState = useSetRecoilState(siteModifyModalRecoil);

  const [name, setName] = useState<string>('');
  const [usuallyName, setUsuallyName] = useState<string>('');
  const [nameDuplication, setNameDuplication] = useState<boolean>(true);
  const [description, setDescription] = useState<string>('');
  const [charge, setCharge] = useState<string>('');
  const [phone, setPhone] = useState<string>('');

  const fetchSiteModify = async () => {
    try {
      const { data } = await siteApi.siteControllerGetSite(id, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      setName(data.name);
      setUsuallyName(data.name);
      setDescription(data.description);
      setCharge(data.charge);
      setPhone(data.phone);
    } catch (err: any) {}
  };

  const handleNameDuplication = async () => {
    if (name.trim() === '') {
      setErrorModalText('사이트명을 입력해 주세요.');
      return;
    }

    const dto: SiteControllerCheckSiteParams = {
      name,
    };

    try {
      const { data } = await siteApi.siteControllerCheckSite(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      if (data.message === '이미 등록된 사이트입니다.') {
        setErrorModalText('중복된 사이트명입니다.');
        return;
      }
      setNameDuplication(true);
    } catch (err: any) {}
  };

  const handleSubmit = async () => {
    if (!nameDuplication) {
      setErrorModalText('사이트명 중복 확인을 해주세요.');
      return;
    }

    const phoneRegex =
      /^(010\d{8}|02\d{8}|070\d{8}|0507\d{8}|1[5-9]\d{6}|043\d{7})$/;

    let phoneTypeCheck = true;
    if (phone.trim() !== '') {
      phoneTypeCheck = phoneRegex.test(phone);
    }

    if (!phoneTypeCheck) {
      setErrorModalText('연락처 형식을 확인해 주세요.');
      return;
    }

    const dto: UpdateSiteDtoTx = {
      name,
      description,
      charge,
      phone,
    };

    try {
      await siteApi.siteControllerUpdateStie(id, dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      setCompleteModalState(true);
    } catch (err: any) {}
  };

  useEffect(() => {
    fetchSiteModify();
  }, []);

  return (
    <>
      <ModalType1 width={560}>
        <div className="title-box flex aic jcsb">
          <h1>사이드 수정</h1>
          <button
            className="x-btn img-box"
            onClick={() => setSiteModifyModalState(false)}
          >
            <img src="/img/ico_x_444.svg" alt="" />
          </button>
        </div>
        <div className="cnt-area flex fdc">
          <div className="cnt-wrap flex fdc">
            <div className="cnt-box flex fdc">
              <div className="cnt-title-box flex jcsb aie">
                <div className="left-box">
                  <h2 className="tm">기본 정보</h2>
                </div>
                <div className="right-box flex">
                  <h3>
                    <span className="inblock">*</span>필수 항목
                  </h3>
                </div>
              </div>
              <CS.TableType2>
                <table>
                  <tbody>
                    <tr>
                      <th>
                        사이트명<span className="star-point">*</span>
                      </th>
                      <td colSpan={3}>
                        <div className="td-box">
                          <IS.TextInputType1
                            style={{ width: 190 }}
                            type="text"
                            value={name}
                            onChange={(e) => {
                              setName(e.target.value);
                              if (e.target.value === usuallyName) {
                                setNameDuplication(true);
                              } else {
                                setNameDuplication(false);
                              }
                            }}
                          />
                          <BS.Btn3Type3
                            type="button"
                            onClick={handleNameDuplication}
                            disabled={nameDuplication}
                          >
                            {nameDuplication ? '완료' : '중복 조회'}
                          </BS.Btn3Type3>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>설명</th>
                      <td colSpan={3}>
                        <IS.TextInputType1
                          style={{ width: 388 }}
                          type="text"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>담당자</th>
                      <td colSpan={3}>
                        <IS.TextInputType1
                          style={{ width: 190 }}
                          type="text"
                          value={charge}
                          onChange={(e) => setCharge(e.target.value)}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>연락처</th>
                      <td colSpan={3}>
                        <IS.TextInputType1
                          style={{ width: 190 }}
                          type="text"
                          value={phone}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d*$/.test(value)) {
                              setPhone(value);
                            }
                          }}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </CS.TableType2>
            </div>
          </div>
          <div className="btn-wrap">
            <BS.Btn2Type3
              type="button"
              onClick={() => setSiteModifyModalState(false)}
            >
              취소
            </BS.Btn2Type3>
            <BS.Btn2Type4 type="button" onClick={handleSubmit}>
              등록
            </BS.Btn2Type4>
          </div>
        </div>
      </ModalType1>
    </>
  );
};

export default SiteModifyModal;
